import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Link as LinkIcon, Code, Brain, ChevronDown, Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useContent, useLanguage, useUI, useLocalPath } from '../context/LanguageContext';

const iconMap = { Search, Link: LinkIcon, Code, Brain };

const ServiceCard = ({ service, index, ui }) => {
  const [isOpen, setIsOpen] = useState(false);
  const IconComponent = iconMap[service.icon];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-[#161b22] border border-[#21262d] rounded-lg overflow-hidden hover:border-[#3b82f6]/30 transition-all duration-500"
    >
      <button onClick={() => setIsOpen(!isOpen)} className="w-full p-6 lg:p-8 text-left group">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-10 h-10 rounded-lg border border-[#21262d] flex items-center justify-center bg-[#0d1117] group-hover:border-[#3b82f6]/30 transition-colors duration-300 flex-shrink-0">
                <IconComponent size={18} className="text-[#3b82f6]" />
              </div>
              <span className="font-mono text-[11px] text-[#7d8590]">// {service.number}</span>
            </div>
            <h3 className="text-xl font-bold text-[#e6edf3] mb-2 group-hover:text-[#3b82f6] transition-colors duration-300 uppercase tracking-wide">
              {service.title}
            </h3>
            <p className="font-mono text-[12px] text-[#3b82f6] mb-3">{service.subtitle}</p>
            <p className="font-mono text-[11px] leading-[1.7] text-[#7d8590] max-w-2xl">{service.description}</p>
          </div>
          <ChevronDown size={18} className={`text-[#7d8590] group-hover:text-[#3b82f6] transition-all duration-300 flex-shrink-0 mt-1 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
            <div className="px-6 lg:px-8 pb-6 lg:pb-8 border-t border-[#21262d] pt-6">
              <div className="grid lg:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-mono text-[11px] tracking-[0.15em] text-[#3b82f6] uppercase mb-4">{ui.outcomes}</h4>
                  <div className="space-y-3">
                    {service.outcomes.map((outcome) => (
                      <div key={outcome} className="flex items-start gap-2">
                        <div className="w-4 h-4 rounded-full border border-[#3b82f6]/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check size={9} className="text-[#3b82f6]" />
                        </div>
                        <p className="font-mono text-[11px] leading-[1.6] text-[#7d8590]">{outcome}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-mono text-[11px] tracking-[0.15em] text-[#3b82f6] uppercase mb-4">{ui.realWorldProof}</h4>
                  <div className="space-y-3">
                    {service.useCases.map((useCase) => (
                      <div key={useCase} className="flex items-start gap-2">
                        <span className="font-mono text-[10px] text-[#3b82f6] mt-0.5 flex-shrink-0">&rarr;</span>
                        <p className="font-mono text-[11px] leading-[1.6] text-[#7d8590]">{useCase}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-6">
                {service.tags.map((tag) => (
                  <span key={tag} className="font-mono text-[10px] tracking-[0.05em] text-[#3b82f6] border border-[#3b82f6]/20 bg-[#3b82f6]/5 px-3 py-1.5 rounded">{tag}</span>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const ServicesPage = () => {
  const content = useContent();
  const localPath = useLocalPath();
  const ui = useUI();

  return (
    <div className="pt-[72px]">
      <section className="relative py-24 lg:py-32" style={{ background: 'radial-gradient(ellipse at 20% 50%, rgba(59,130,246,0.04) 0%, #0d1117 70%)' }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <span className="font-mono text-[11px] tracking-[0.2em] text-[#3b82f6] uppercase block mb-4">{ui.servicesLabel}</span>
                <h1 className="text-[clamp(36px,5vw,72px)] font-black leading-[0.95] tracking-[-0.02em] text-[#e6edf3] uppercase">
                  {ui.whatWeActuallyDo[0]}<br />
                  <span className="text-[#3b82f6]">{ui.whatWeActuallyDo[1]}</span>
                </h1>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="flex items-end">
              <p className="font-mono text-[13px] leading-[1.8] text-[#7d8590] max-w-md">{ui.servicesDescription}</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-[#21262d]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="space-y-4">
            {content.services.map((service) => (
              <ServiceCard key={service.title} service={service} index={content.services.indexOf(service)} ui={ui} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-[#21262d]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-2xl font-black text-[#e6edf3]">
                  {ui.wantToSeeInAction[0]}<span className="text-[#3b82f6]">{ui.wantToSeeInAction[1]}</span>
                </h2>
                <p className="font-mono text-[12px] leading-[1.7] text-[#7d8590] mt-3">{ui.servicesCtaDesc}</p>
              </div>
              <div className="flex items-center gap-4 lg:justify-end flex-wrap">
                <Link to={localPath('/projects/')} className="font-mono text-[11px] tracking-[0.1em] uppercase bg-[#3b82f6] text-white px-8 py-3.5 rounded-full hover:bg-[#2563eb] transition-colors duration-300 font-semibold flex items-center gap-2">
                  {ui.viewPlatforms}
                  <ArrowRight size={13} />
                </Link>
                <a href="mailto:info@go2digital.com" className="font-mono text-[11px] tracking-[0.1em] uppercase text-[#3b82f6] border border-[#3b82f6]/50 px-6 py-3.5 rounded-full hover:bg-[#3b82f6]/10 transition-all duration-300">
                  {ui.contactUs}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
